import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import FileUploader from "@/components/admin/FileUploader";

const LANGS = ["en", "es", "ru"];
const LANG_FIELDS = [
  { key: "heroTitle", label: "Hero title" },
  { key: "heroSubtitle", label: "Hero subtitle", textarea: true },
  { key: "heroCTA", label: "Hero CTA text" },
  { key: "contactText", label: "Contact text", textarea: true },
  { key: "address", label: "Address" },
  { key: "openingHours", label: "Opening hours" },
  { key: "rentalConditions", label: "Rental conditions", textarea: true },
  { key: "termsConditions", label: "Terms & conditions", textarea: true },
  { key: "privacyPolicy", label: "Privacy policy", textarea: true },
  { key: "cookiesPolicy", label: "Cookies policy", textarea: true },
];

export default function SettingsAdmin() {
  const [s, setS] = useState({});
  const [activeLang, setActiveLang] = useState("en");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => { const { data } = await api.get("/settings"); setS(data || {}); })();
  }, []);

  const save = async () => {
    const { data } = await api.put("/settings", s);
    setS(data); setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };
  // Use functional updaters so back-to-back set() calls (e.g. when a file uploader
  // updates BOTH the media url and the type) don't overwrite each other through a
  // stale closure of `s`.
  const set = (k, v) => setS((prev) => ({ ...prev, [k]: v }));
  const setLang = (k, lang, v) =>
    setS((prev) => ({ ...prev, [k]: { ...(prev[k] || {}), [lang]: v } }));
  const fullUrl = (u) => (u && !u.startsWith("http") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

  return (
    <div data-testid="admin-settings-page">
      <h1 className="font-display text-[#F5F1EB] text-3xl mb-8">Settings</h1>

      <h2 className="text-overline text-[#C9A55A] mb-3">Contact</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <Field label="Website phone (shown on site)" value={s.websitePhone || ""} onChange={(v) => set("websitePhone", v)} testid="set-websitePhone" />
        <Field label="Website WhatsApp (used by reservation buttons)" value={s.websiteWhatsapp || ""} onChange={(v) => set("websiteWhatsapp", v)} testid="set-websiteWhatsapp" />
        <Field label="Website WhatsApp wa.me digits" value={s.websiteWhatsappWaMe || ""} onChange={(v) => set("websiteWhatsappWaMe", v.replace(/[^0-9]/g, ""))} testid="set-websiteWhatsappWaMe" />
        <Field label="Email" value={s.email || ""} onChange={(v) => set("email", v)} testid="set-email" />
      </div>
      <p className="text-xs text-[#8A8A8A] mb-6">
        Website WhatsApp is the number used by every reservation button on the website. Update both the human format and the wa.me digits.
      </p>

      <h2 className="text-overline text-[#C9A55A] mb-3">Social media (reference only)</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <Field label="Social phone" value={s.socialPhone || ""} onChange={(v) => set("socialPhone", v)} testid="set-socialPhone" />
        <Field label="Social WhatsApp" value={s.socialWhatsapp || ""} onChange={(v) => set("socialWhatsapp", v)} testid="set-socialWhatsapp" />
        <Field label="Social WhatsApp wa.me digits" value={s.socialWhatsappWaMe || ""} onChange={(v) => set("socialWhatsappWaMe", v.replace(/[^0-9]/g, ""))} testid="set-socialWhatsappWaMe" />
      </div>
      <p className="text-xs text-[#8A8A8A] mb-8">
        Social WhatsApp is the number used in social media profiles (Instagram, Facebook, TikTok, YouTube). It is never opened from the website.
      </p>

      <h2 className="text-overline text-[#C9A55A] mb-3">Hero</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
        <div>
          <Label>Hero video (paste URL or upload below)</Label>
          <input value={s.heroVideo || ""} onChange={(e) => set("heroVideo", e.target.value)}
            data-testid="set-hero-video"
            className="w-full bg-[#0C0C0C] border border-[#2A2A2A] focus:border-[#C9A55A] text-[#F5F1EB] px-3 h-10 text-sm mb-2" />
          <FileUploader accept="video/*" onUploaded={(r) => set("heroVideo", fullUrl(r.url))} label="Upload hero video (max 200 MB)" testid="upload-hero-video" />
          {s.heroVideo && (
            <video src={s.heroVideo} controls className="mt-3 w-full max-h-48 bg-[#1a1a1a] border border-[#2A2A2A]" />
          )}
        </div>
        <div>
          <Label>Hero poster image (fallback for mobile)</Label>
          <input value={s.heroPoster || ""} onChange={(e) => set("heroPoster", e.target.value)}
            data-testid="set-hero-poster"
            className="w-full bg-[#0C0C0C] border border-[#2A2A2A] focus:border-[#C9A55A] text-[#F5F1EB] px-3 h-10 text-sm mb-2" />
          <FileUploader accept="image/*" onUploaded={(r) => { set("heroPoster", fullUrl(r.url)); set("heroFallbackImage", fullUrl(r.url)); }}
            label="Upload poster image" testid="upload-hero-poster" />
          {s.heroPoster && (
            <img src={s.heroPoster} alt="" className="mt-3 w-full max-h-48 object-cover bg-[#1a1a1a] border border-[#2A2A2A]" />
          )}
        </div>
      </div>

      <h2 className="text-overline text-[#C9A55A] mb-3 mt-8">&ldquo;Ready to drive?&rdquo; media (after Routes section)</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
        <div>
          <Label>Media type</Label>
          <select value={s.ctaMediaType || ""} onChange={(e) => set("ctaMediaType", e.target.value)}
            data-testid="set-cta-type"
            className="w-full bg-[#0C0C0C] border border-[#2A2A2A] focus:border-[#C9A55A] text-[#F5F1EB] px-3 h-10 text-sm">
            <option value="">— None —</option>
            <option value="image">Image</option>
            <option value="video">Video</option>
          </select>
          <input value={s.ctaMediaUrl || ""} onChange={(e) => set("ctaMediaUrl", e.target.value)}
            placeholder="Media URL"
            data-testid="set-cta-url"
            className="w-full bg-[#0C0C0C] border border-[#2A2A2A] focus:border-[#C9A55A] text-[#F5F1EB] px-3 h-10 text-sm mt-2" />
          <div className="grid grid-cols-2 gap-2 mt-2">
            <FileUploader accept="image/*" onUploaded={(r) => { set("ctaMediaUrl", fullUrl(r.url)); set("ctaMediaType", "image"); }}
              label="Upload image" testid="upload-cta-image" />
            <FileUploader accept="video/*" onUploaded={(r) => { set("ctaMediaUrl", fullUrl(r.url)); set("ctaMediaType", "video"); }}
              label="Upload video" testid="upload-cta-video" />
          </div>
        </div>
        <div>
          <Label>Video poster (only for videos)</Label>
          <input value={s.ctaMediaPoster || ""} onChange={(e) => set("ctaMediaPoster", e.target.value)}
            data-testid="set-cta-poster"
            className="w-full bg-[#0C0C0C] border border-[#2A2A2A] focus:border-[#C9A55A] text-[#F5F1EB] px-3 h-10 text-sm mb-2" />
          <FileUploader accept="image/*" onUploaded={(r) => set("ctaMediaPoster", fullUrl(r.url))} label="Upload poster" testid="upload-cta-poster" />
          {s.ctaMediaUrl && s.ctaMediaType === "image" && (
            <img src={s.ctaMediaUrl} alt="" className="mt-3 w-full max-h-48 object-cover bg-[#1a1a1a] border border-[#2A2A2A]" />
          )}
          {s.ctaMediaUrl && s.ctaMediaType === "video" && (
            <video src={s.ctaMediaUrl} poster={s.ctaMediaPoster} controls className="mt-3 w-full max-h-48 bg-[#1a1a1a] border border-[#2A2A2A]" />
          )}
        </div>
      </div>

      <h2 className="text-overline text-[#C9A55A] mb-3 mt-8">Translations (EN / ES / RU)</h2>
      <div className="border border-[#2A2A2A]">
        <div className="flex border-b border-[#2A2A2A]">
          {LANGS.map((l) => (
            <button key={l} onClick={() => setActiveLang(l)}
              className={`px-4 py-2 text-xs tracking-[0.18em] uppercase font-semibold ${activeLang === l ? "bg-[#C9A55A] text-[#0C0C0C]" : "text-[#F5F1EB]"}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="p-5 space-y-4">
          {LANG_FIELDS.map((f) => (
            <Field key={f.key} label={`${f.label} (${activeLang})`}
              value={s[f.key]?.[activeLang] || ""}
              onChange={(v) => setLang(f.key, activeLang, v)}
              textarea={f.textarea}
              testid={`set-${f.key}-${activeLang}`} />
          ))}
        </div>
      </div>

      <div className="mt-8 flex items-center gap-4">
        <button onClick={save} className="btn-gold" data-testid="settings-save">Save</button>
        {saved && <span className="text-[#C9A55A] text-sm">Saved</span>}
      </div>
    </div>
  );
}

const Label = ({ children }) => <label className="text-[10px] text-[#8A8A8A] tracking-[0.18em] uppercase block mb-1">{children}</label>;
function Field({ label, value, onChange, textarea, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      {textarea
        ? <textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
            className="w-full bg-[#0C0C0C] border border-[#2A2A2A] focus:border-[#C9A55A] text-[#F5F1EB] px-3 py-2 text-sm" />
        : <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
            className="w-full bg-[#0C0C0C] border border-[#2A2A2A] focus:border-[#C9A55A] text-[#F5F1EB] px-3 h-10 text-sm" />}
    </div>
  );
}
